import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw, ExternalLink, Smartphone, Monitor, Maximize } from 'lucide-react';
import { CHATBOT_API_BASE } from '@/services/api/chatbotClient';

interface Props {
    subdomain: string;
    path: string;
}

const DEVICES = [
    { name: 'Mobile', icon: <Smartphone className="w-3 h-3" />, w: 380, h: 720 },
    { name: 'Tablet', icon: <Monitor className="w-3 h-3" />, w: 700, h: 720 },
    { name: 'Desktop', icon: <Maximize className="w-3 h-3" />, w: 1100, h: 720 },
];

export default function ChatPreview({ subdomain, path }: Props) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [tracking, setTracking] = useState('endpoint=ln&country=BR&keyword=teste');
    const [device, setDevice] = useState(DEVICES[0]);

    const url = `${CHATBOT_API_BASE}/chat?` + new URLSearchParams({
        host: subdomain,
        path,
    }).toString() + (tracking ? `&${tracking}` : '');

    const reload = () => {
        if (iframeRef.current) iframeRef.current.src = url + '&_t=' + Date.now();
    };

    return (
        <div className="space-y-3">
            <div className="bg-white border rounded-lg p-3 flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Tracking params (query string)</Label>
                    <Input value={tracking} onChange={(e) => setTracking(e.target.value)}
                        placeholder="endpoint=ln&country=BR&keyword=loan" />
                </div>
                <div className="flex gap-1">
                    {DEVICES.map(d => (
                        <button key={d.name} onClick={() => setDevice(d)}
                            className={`flex items-center gap-1 px-3 py-2 border rounded text-xs ${
                                device.name === d.name ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''
                            }`}>
                            {d.icon}{d.name}
                        </button>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={reload}>
                    <RefreshCw className="w-4 h-4 mr-1" />Recarregar
                </Button>
                <Button variant="outline" size="sm" onClick={() => window.open(url, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-1" />Abrir
                </Button>
            </div>

            <div className="bg-slate-100 p-6 rounded-lg flex justify-center overflow-auto">
                <div style={{
                    width: device.w, height: device.h,
                    boxShadow: '0 6px 24px rgba(0,0,0,.12)', borderRadius: 8,
                    overflow: 'hidden', background: '#fff',
                }}>
                    <iframe ref={iframeRef} src={url}
                        style={{ width: '100%', height: '100%', border: 0 }}
                        title="chatbot preview" />
                </div>
            </div>

            <div className="text-xs text-slate-500 font-mono bg-slate-50 p-2 rounded break-all">
                {url}
            </div>
        </div>
    );
}
